import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventTubularsQuery } from "@api/pApi";
import { useGetCatalogQuery } from "@api/mApi";
import { useUnits } from "@units/useUnits";
import type { TubularStringOut, PipeTallyRowOut } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import { WellboreSchematic, type SchematicString } from "./WellboreSchematic";
import "./tubulars.css";

type TubTab = "strings" | "tally";

/**
 * Tubulars panel (§6) — the casing/liner strings + pipe tally + wellbore
 * schematic for an event. Reads GET /v1/events/{event_id}/tubulars (assemblies
 * + casing + pipe_tally). Depths are display-converted via useUnits against the
 * event's unit profile; `hole_size` / OD are inches (per-field UOM override).
 * The FE never recomputes a tally — cumulative length is served.
 */
export function TubularsPanel({
  eventId,
  unitProfileKey,
}: {
  eventId: string;
  unitProfileKey?: string | null;
}) {
  const { t } = useLocale();
  const { convertField, fmt } = useUnits(unitProfileKey);
  const [tab, setTab] = useState<TubTab>("strings");

  const { data, isLoading, isError, refetch } = useGetEventTubularsQuery(eventId, {
    skip: !eventId,
  });

  // Catalog label maps (code → display label). Read-only Oxbo-M value lookups.
  const stringTypes = useCatalogLabels("string_types");
  const grades = useCatalogLabels("grades");
  const connections = useCatalogLabels("connections");
  const sizes = useCatalogLabels("nominal_sizes");

  // All strings (casing + assemblies), casing first (outer program), then any
  // running assemblies. The schematic keys off the casing/liner program.
  // `casing` / `assemblies` are arrays on the mock but can be absent/mis-shaped
  // on a real/partial response even when `data` itself is truthy — coalesce each.
  const casing = useMemo<TubularStringOut[]>(
    () => (Array.isArray(data?.casing) ? data.casing : []),
    [data],
  );
  const assemblies = useMemo<TubularStringOut[]>(
    () => (Array.isArray(data?.assemblies) ? data.assemblies : []),
    [data],
  );
  const strings = useMemo<TubularStringOut[]>(
    () => [...casing, ...assemblies],
    [casing, assemblies],
  );
  const tally = useMemo(() => (Array.isArray(data?.pipe_tally) ? data.pipe_tally : []), [data]);

  // The "active" string: the one being run now, else the last casing string.
  const activeString = useMemo(
    () => strings.find((s) => s.running) ?? casing[casing.length - 1] ?? strings[0],
    [strings, casing],
  );

  const [selectedStringId, setSelectedStringId] = useState<string | null>(null);
  const tallyStringId = selectedStringId ?? activeString?.id ?? null;
  const tallyString = strings.find((s) => s.id === tallyStringId);
  const stringTally = useMemo(
    () =>
      tally.filter((r) => r.string_id === tallyStringId).sort((a, b) => a.joint_no - b.joint_no),
    [tally, tallyStringId],
  );

  // ── KPIs ── (deepest shoe = active shoe; total = sum of served cumulative)
  const activeShoeMd = activeString?.shoe_md ?? null;
  const activeShoeTvd = activeString?.shoe_tvd ?? null;
  const totalLength = useMemo(() => {
    // Sum the deepest cumulative per string (served); fall back to shoe span.
    const byString = new Map<string, number>();
    for (const r of tally) {
      const cur = byString.get(r.string_id) ?? 0;
      byString.set(r.string_id, Math.max(cur, r.cum_length ?? 0));
    }
    let sum = 0;
    for (const s of strings) sum += byString.get(s.id) ?? s.shoe_md ?? 0;
    return sum;
  }, [tally, strings]);

  const jointsRunToday = activeString
    ? tally.filter((r) => r.string_id === activeString.id).length
    : 0;

  const dLen = (table: string, col: string, v: number | null | undefined, digits = 0) =>
    fmt(convertField(table, col, v, unitProfileKey), { maximumFractionDigits: digits });

  const stats: Stat[] = data
    ? [
        {
          label: t("tub.kpi.totalLength"),
          value: fmt(convertField("pipe_tally", "cum_length", totalLength, unitProfileKey), {
            maximumFractionDigits: 0,
          }),
        },
        { label: t("tub.kpi.shoeMd"), value: dLen("tubular_string", "shoe_md", activeShoeMd) },
        { label: t("tub.kpi.shoeTvd"), value: dLen("tubular_string", "shoe_tvd", activeShoeTvd) },
        { label: t("tub.kpi.jointsRun"), value: String(jointsRunToday), tone: "good" },
      ]
    : [];

  // Build the schematic model (labels + display-converted depths).
  const schematicStrings = useMemo<SchematicString[]>(
    () =>
      data
        ? [...casing]
            .filter((s) => s.shoe_md != null)
            .map((s) => ({
              id: s.id,
              name: stringTypes.get(s.string_type_entry_key) ?? s.string_type_entry_key,
              odIn: odInches(s.nominal_size_entry_key, sizes),
              shoeMd: s.shoe_md,
              shoeMdLabel: dLen("tubular_string", "shoe_md", s.shoe_md),
              shoeTvdLabel:
                s.shoe_tvd != null ? dLen("tubular_string", "shoe_tvd", s.shoe_tvd) : "",
              gradeLabel: s.grade_entry_key
                ? (grades.get(s.grade_entry_key) ?? s.grade_entry_key)
                : "",
              connectionLabel: s.connection_entry_key
                ? (connections.get(s.connection_entry_key) ?? s.connection_entry_key)
                : "",
              running: s.running,
              carried: s.carry_over,
            }))
        : [],
    // dLen closes over convertField/fmt which are memoised in useUnits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, stringTypes, sizes, grades, connections, unitProfileKey],
  );

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && strings.length === 0}
      emptyMessage={t("tub.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="tabbar" role="tablist" aria-label={t("tub.title")} style={{ marginTop: 14 }}>
        {(
          [
            { id: "strings", key: "tub.tab.strings" },
            { id: "tally", key: "tub.tab.tally" },
          ] as { id: TubTab; key: MessageKey }[]
        ).map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            className={tab === tb.id ? "active" : ""}
            onClick={() => setTab(tb.id)}
          >
            {t(tb.key)}
          </button>
        ))}
      </div>

      <div className="tab-panel" role="tabpanel">
        {tab === "strings" ? (
          <StringsTable
            strings={strings}
            activeId={activeString?.id ?? null}
            stringTypes={stringTypes}
            grades={grades}
            connections={connections}
            sizes={sizes}
            dLen={dLen}
            onSelect={(id) => {
              setSelectedStringId(id);
              setTab("tally");
            }}
          />
        ) : (
          <PipeTallyTable
            rows={stringTally}
            headerName={
              tallyString
                ? (stringTypes.get(tallyString.string_type_entry_key) ??
                  tallyString.string_type_entry_key)
                : null
            }
            headerSize={tallyString ? odLabel(tallyString.nominal_size_entry_key, sizes) : null}
            dLen={dLen}
          />
        )}
      </div>

      <div className="card schematic-card" style={{ marginTop: 14 }}>
        <div className="panel-head-row">
          <div>
            <h3>{t("tub.schematic.title")}</h3>
            <p className="hint">{t("tub.schematic.subtitle")}</p>
          </div>
        </div>
        <div className="svg-wrap">
          <WellboreSchematic
            strings={schematicStrings}
            ariaLabel={buildSchematicAria(schematicStrings)}
            labels={{
              wellhead: t("tub.schematic.wellhead"),
              openHole: t("tub.schematic.openHole"),
              bitDepth: t("tub.schematic.bitDepth"),
              shoeMd: t("tub.col.shoeMd"),
              shoeTvd: t("tub.col.shoeTvd"),
              running: t("tub.status.running"),
            }}
          />
        </div>
      </div>
    </QueryState>
  );
}

// ─────────────────────────── Strings table ───────────────────────────
type LabelMap = Map<string, string>;
type DLen = (table: string, col: string, v: number | null | undefined, digits?: number) => string;

function StringsTable({
  strings,
  activeId,
  stringTypes,
  grades,
  connections,
  sizes,
  dLen,
  onSelect,
}: {
  strings: TubularStringOut[];
  activeId: string | null;
  stringTypes: LabelMap;
  grades: LabelMap;
  connections: LabelMap;
  sizes: LabelMap;
  dLen: DLen;
  onSelect: (id: string) => void;
}) {
  const { t } = useLocale();
  if (strings.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">{t("tub.noStrings")}</div>
      </div>
    );
  }
  return (
    <div className="card">
      <p className="hint" style={{ marginBottom: 10 }}>
        {t("tub.strings.caption")}
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("tub.col.string")}</th>
              <th className="num">{t("tub.col.size")}</th>
              <th>{t("tub.col.grade")}</th>
              <th>{t("tub.col.connection")}</th>
              <th className="num">{t("tub.col.shoeMd")}</th>
              <th className="num">{t("tub.col.shoeTvd")}</th>
              <th>{t("tub.col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {strings.map((s) => {
              const active = s.id === activeId;
              return (
                <tr
                  key={s.id}
                  data-owner="p"
                  className={[s.carry_over ? "carried" : "", active ? "row-active" : ""]
                    .filter(Boolean)
                    .join(" ")}
                  aria-current={active ? "true" : undefined}
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelect(s.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(s.id);
                    }
                  }}
                >
                  <td>{stringTypes.get(s.string_type_entry_key) ?? s.string_type_entry_key}</td>
                  <td className="num tab-nums">{odLabel(s.nominal_size_entry_key, sizes)}</td>
                  <td>
                    {s.grade_entry_key ? (grades.get(s.grade_entry_key) ?? s.grade_entry_key) : "—"}
                  </td>
                  <td>
                    {s.connection_entry_key
                      ? (connections.get(s.connection_entry_key) ?? s.connection_entry_key)
                      : "—"}
                  </td>
                  <td className="num tab-nums">{dLen("tubular_string", "shoe_md", s.shoe_md)}</td>
                  <td className="num tab-nums">{dLen("tubular_string", "shoe_tvd", s.shoe_tvd)}</td>
                  <td>
                    <StringStatus running={s.running} carried={s.carry_over} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StringStatus({ running, carried }: { running: boolean; carried: boolean }) {
  const { t } = useLocale();
  if (running) return <span className="badge status-drilling">{t("tub.status.running")}</span>;
  if (carried)
    return (
      <>
        <span className="badge status-producing">{t("tub.status.set")}</span>{" "}
        <span className="badge">{t("tub.carried")}</span>
      </>
    );
  return <span className="badge">{t("tub.status.planned")}</span>;
}

// ─────────────────────────── Pipe tally ───────────────────────────
function PipeTallyTable({
  rows,
  headerName,
  headerSize,
  dLen,
}: {
  rows: PipeTallyRowOut[];
  headerName: string | null;
  headerSize: string | null;
  dLen: DLen;
}) {
  const { t } = useLocale();
  if (!headerName) {
    return (
      <div className="card">
        <div className="empty-state">{t("tub.tally.pick")}</div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="panel-head-row">
        <p className="hint">
          {t("tub.tally.for")} <b>{headerName}</b>
          {headerSize ? ` · ${headerSize}` : ""} · {t("tub.tally.caption")}
        </p>
      </div>
      {rows.length === 0 ? (
        <div className="empty-state">{t("tub.noTally")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("tub.col.jt")}</th>
                <th className="num">{t("tub.col.length")}</th>
                <th className="num">{t("tub.col.cum")}</th>
                <th>{t("tub.col.serial")}</th>
                <th>{t("tub.col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const last = i === rows.length - 1;
                return (
                  <tr key={r.id} data-owner="p">
                    <td className="num tab-nums">{r.joint_no}</td>
                    <td className="num tab-nums">{dLen("pipe_tally", "length", r.length, 1)}</td>
                    <td className="num tab-nums">
                      {r.cum_length == null
                        ? "—"
                        : dLen("pipe_tally", "cum_length", r.cum_length, 1)}
                    </td>
                    <td className="mono">{r.serial_no ?? "—"}</td>
                    <td>
                      {last ? (
                        <span className="badge status-drilling">{t("tub.tally.runningJoint")}</span>
                      ) : (
                        <span className="badge status-producing">{t("tub.tally.run")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── helpers ───────────────────────────

/** Read an Oxbo-M value catalog into a code → label map. */
function useCatalogLabels(key: string): LabelMap {
  const { data } = useGetCatalogQuery({ key });
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const v of data?.values ?? []) m.set(v.code, v.label);
    return m;
  }, [data]);
}

/** OD in inches from a nominal_sizes entry (attrs.od_in), else parse the code. */
function odInches(code: string, sizes: LabelMap): number | null {
  const n = Number(code);
  if (Number.isFinite(n)) return n;
  // Fallback: the label may be the display size; leave null if unparseable.
  const lbl = sizes.get(code);
  const m = lbl?.match(/([\d.]+)/);
  return m ? Number(m[1]) : null;
}

/** Human OD label — the catalog display label ("9⅝″") or the raw code. */
function odLabel(code: string, sizes: LabelMap): string {
  return sizes.get(code) ?? code;
}

/** A descriptive aria-label listing the drawn strings + shoe depths. */
function buildSchematicAria(strings: SchematicString[]): string {
  if (strings.length === 0) return "Wellbore schematic: no strings";
  const parts = strings.map(
    (s) =>
      `${s.name}${s.odIn != null ? ` ${s.odIn} inch` : ""} to ${s.shoeMdLabel}${s.running ? " (running)" : ""}`,
  );
  return `Wellbore schematic: ${parts.join(", ")}`;
}
